import { getLocalStorageItem } from "@utils/index";
import { USER_SESSION_KEY } from "@hooks/use-auth-context/utils";
import {
  localStorageAdapter,
  TableStatePersistenceAdapter,
} from "./storage-adapter";

export interface PersistedTableFilterState<TFilter> {
  version: number;
  filters: TFilter[];
  hiddenColumns: string[];
  perPage: number;
}

const KEY_PREFIX = "sm_filters";

// Reads the logged-in user's email straight from the stored session rather
// than through UserContext, which is still `null` on the very first render
// after a refresh (hydrated later via effect) — see repo memory notes on the
// token-refresh "screen refresh" bug for the same class of hydration race.
// The stored session nests the login response's `user` object (see
// use-auth-context/index.tsx), so email lives at `.user.email`, with a flat
// `.email` fallback matching the same defensive check used there.
const getStoredUserEmail = (): string => {
  const stored = getLocalStorageItem(USER_SESSION_KEY);
  const email = stored?.email ?? stored?.user?.email;
  return typeof email === "string" ? email.toLowerCase() : "";
};

/**
 * Collision-safe across tables/subsites/users. `instanceKey` additionally
 * scopes multiple simultaneous mounts of the same table — e.g. the full-page
 * Assets table vs. one or more Asset table dashboard widgets — so they don't
 * overwrite each other's persisted filters. Returns null until both the
 * subsite and a logged-in user are resolved, so nothing is read/written to an
 * unscoped bucket that a different user/site could later inherit.
 */
export function buildTableFilterStorageKey(
  tableKey: string,
  siteId: string,
  instanceKey?: string
): string | null {
  const email = getStoredUserEmail();
  if (!siteId || !email) return null;
  const scopedTableKey = instanceKey ? `${tableKey}:${instanceKey}` : tableKey;
  return `${KEY_PREFIX}:${scopedTableKey}:${siteId}:${encodeURIComponent(email)}`;
}

export function loadPersistedTableFilterState<TFilter>(
  tableKey: string,
  siteId: string,
  schemaVersion: number,
  instanceKey?: string,
  adapter: TableStatePersistenceAdapter = localStorageAdapter
): PersistedTableFilterState<TFilter> | null {
  const key = buildTableFilterStorageKey(tableKey, siteId, instanceKey);
  if (!key) return null;
  const stored = adapter.get<PersistedTableFilterState<TFilter>>(key);
  // A schema-version mismatch means a filter field was renamed/removed since
  // this was stored — discard rather than silently reapply a stale filter.
  if (!stored || stored.version !== schemaVersion) return null;
  return stored;
}

export function savePersistedTableFilterState<TFilter>(
  tableKey: string,
  siteId: string,
  schemaVersion: number,
  state: Omit<PersistedTableFilterState<TFilter>, "version">,
  instanceKey?: string,
  adapter: TableStatePersistenceAdapter = localStorageAdapter
): void {
  const key = buildTableFilterStorageKey(tableKey, siteId, instanceKey);
  if (!key) return;
  adapter.set(key, { version: schemaVersion, ...state });
}
